import * as React from 'react';
import type { Padding, Placement } from '@popperjs/core';
import type { PopperOptions, Config } from 'react-popper-tooltip';
import { usePopperTooltip } from 'react-popper-tooltip';
import Portal from '../Portal/Portal';
import type { Props as PortalProps } from '../Portal/Portal';
import cn from '~modules/cn';

import './tooltip.scss';

type TooltipAppearanceClassNames =
    | 'tooltip--no-background'
    | 'tooltip--no-max-width'
    | 'tooltip--no-padding'
    | 'tooltip--left-0'
    | 'tooltip--box-shadow'
    | 'tooltip--default-max-height';

export type TooltipProps = {
    tooltipProps?: Config;
    popperProps?: PopperOptions;
    for?: React.RefObject<$TSFixMe>;
    children?: React.ReactElement;
    appearanceClassNames?: TooltipAppearanceClassNames[];
    className?: string;
    offset?: number[];
    padding?: TooltipPadding;
    placement?: TooltipPlacement;
    host?: PortalProps['host'];
    sameWidth?: boolean;
    appendParent?: boolean;
};

export type TooltipPadding = Padding;

export type TooltipPlacement = Placement;

const Tooltip = (props: TooltipProps): JSX.Element => {
    const { getTooltipProps, setTooltipRef, triggerRef, setTriggerRef, visible, state } = usePopperTooltip(
        {
            delayShow: 500,
            ...(props.tooltipProps || {})
        },
        {
            ...(props.popperProps || {}),
            placement: props.placement,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: props.offset ?? [0, 5]
                    }
                },
                {
                    name: 'preventOverflow',
                    options: {
                        padding: props.padding
                    }
                },
                ...(props.popperProps?.modifiers ?? [])
            ]
        }
    );

    if (triggerRef !== props.for?.current) {
        setTriggerRef(props.for?.current);
    }

    React.useEffect(() => {
        setTriggerRef(props.for?.current);
    }, [props.for, setTriggerRef]);

    return (
        <>
            {visible && (
                <Portal host={props.host || props.for?.current?.ownerDocument?.body || document.body} appendParent={props.appendParent}>
                    <div
                        ref={setTooltipRef}
                        {...getTooltipProps({
                            className: cn('tooltip', props.className, props.appearanceClassNames),
                            style: {
                                opacity: props.for && state ? 1 : 0,
                                minWidth: props.sameWidth ? props.for?.current?.scrollWidth ?? 'none' : 'none'
                            }
                        })}
                    >
                        {props.children}
                    </div>
                </Portal>
            )}
        </>
    );
};

export default Tooltip;
